import { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useLobby, type TableInfo } from '../net/lobby.js';
import { hasAccounts } from '../net/supabase.js';
import { Logo } from './Logo.js';
import { Stats } from './Stats.js';

interface Props {
  name: string;
  error: string | null;
  onSit(code: string): void;
  onBots(): void;
  /** Oturum açmış oyuncunun kullanıcı adı — tabloda kendi satırı vurgulanıyor. */
  username?: string | null;
  /** Kendi plakasına dokununca profil açılıyor. */
  onProfile?: (() => void) | undefined;
  /** Değeri değişince sıralama yeniden okunuyor (profil güncellemesi sonrası). */
  refresh?: number;
}

/**
 * Salon: masaların canlı listesi.
 *
 * Masalar sabit kodlu ve sayıları `TABLE_CODES` ile belirleniyor. Doluluk
 * Colyseus'un salon odasından geliyor, yani başkası oturduğunda liste
 * kendiliğinden güncelleniyor.
 */
export function Lobby({ name, error, onSit, onBots, username, onProfile, refresh = 0 }: Props) {
  const { tables, online } = useLobby(true);
  const [statsOpen, setStatsOpen] = useState(false);
  const setProfileOpen = (open: boolean) => {
    if (open) onProfile?.();
  };

  return (
    <div className="lobby">
      {/* Botlarla oynamak üst çubukta: masa beklemeden oynamanın yolu bu ve
          altta kalınca masaların arkasına düşüyordu. */}
      <header className="lobby__bar">
        <Logo size="sm" />

        <div className="lobby__actions">
          <button type="button" className="btn btn--tiny btn--primary" onClick={onBots}>
            Botlarla oyna
          </button>
          {/* Sıralama ikonu: yükselen üç sütun. Metin yerine ikon, çünkü
              üst çubuktaki yer dar ve yanında zaten iki öğe var. */}
          {hasAccounts && (
            <button
              type="button"
              className="icon-btn"
              onClick={() => setStatsOpen(true)}
              title="Sıralama"
              aria-label="Sıralama"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
                <rect x="3" y="13" width="4.6" height="8" rx="1.2" fill="currentColor" />
                <rect x="9.7" y="8" width="4.6" height="13" rx="1.2" fill="currentColor" />
                <rect x="16.4" y="3.5" width="4.6" height="17.5" rx="1.2" fill="currentColor" />
              </svg>
            </button>
          )}
        </div>

        {/* Profil kapısı: kendi plakan. */}
        <button
          type="button"
          className="lobby__me"
          onClick={() => hasAccounts && setProfileOpen(true)}
          disabled={!hasAccounts}
          title="Profil"
        >
          <span className="lobby__avatar">{name.slice(0, 1).toUpperCase()}</span>
          <span>{name}</span>
        </button>
      </header>

      {!online && <p className="lobby__warn">Sunucuya ulaşılamıyor</p>}
      {error && <p className="error lobby__warn">{error}</p>}

      <div className="lobby__tables">
        {tables.map((table, index) => (
          <TableCard key={table.code} table={table} index={index} onSit={onSit} />
        ))}
      </div>

      <AnimatePresence>
        {/* `key` tazeleme sayacı: profilde ad değişince tablo yeniden okunuyor. */}
        {statsOpen && (
          <Stats key={refresh} me={username ?? null} onClose={() => setStatsOpen(false)} />
        )}
      </AnimatePresence>
    </div>
  );
}

function TableCard({
  table,
  index,
  onSit,
}: {
  table: TableInfo;
  index: number;
  onSit(code: string): void;
}) {
  return (
    <motion.div
      className={`table-card ${table.playing ? 'is-playing' : ''}`}
      initial={{ y: 10, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: index * 0.05 }}
    >
      {/* Yalnızca oynanıyorsa durum yazısı var; boş masada söylenecek bir şey
          yok, boş koltuklar zaten görünüyor. */}
      <div className="table-card__head">
        <span className="table-card__name">MASA {index + 1}</span>
        {table.playing && <span className="table-card__state">Oyunda</span>}
      </div>

      {/* Dört koltuk masanın çevresinde — doluluk tek bakışta okunuyor. */}
      <div className="table-card__felt">
        {Array.from({ length: 4 }, (_, i) => {
          const player = table.players[i];
          const seatClass = `table-card__seat table-card__seat--${i}`;
          if (player) {
            return (
              <span key={i} className={`${seatClass} is-taken`} title={player}>
                {player.slice(0, 2).toUpperCase()}
              </span>
            );
          }
          return (
            <button
              key={i}
              type="button"
              className={seatClass}
              disabled={table.playing}
              onClick={() => onSit(table.code)}
            >
              OTUR
            </button>
          );
        })}
      </div>
    </motion.div>
  );
}
